import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Chip,
  Avatar,
  Stack,
  SelectChangeEvent,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  KeyboardArrowDown,
  Science,
  Person,
  Business,
  Visibility,
  Assignment,
  Analytics,
  CheckCircle,
  ErrorOutline,
} from "@mui/icons-material";
import type { Study } from "../StudyManagement/types/studyManagement.types";
import {
  convertApiPhaseToDisplay,
  API_TO_DISPLAY_PHASE_MAPPING,
} from "../StudyManagement/constants/studyConstants";

type ApiPhaseKey = keyof typeof API_TO_DISPLAY_PHASE_MAPPING;
import { CLINICAL_FONT_FAMILY } from "../../components/shared/constants";
import {
  UserRoleEnum,
  normalizeRole,
  ROLE_DISPLAY_CONFIG,
} from "@/types/role.type";
import { NO_STUDY_SENTINEL } from "@/utils/studyContext";

interface StudyContextSelectorProps {
  studies: Study[];
  selectedStudyId?: string;
  userStudyRoles: Record<string, string>;
  onStudyChange: (studyId: string) => void;
  className?: string;
  /** Show the "No Study Selected" option for PM/CTA platform-level users */
  showNoStudyOption?: boolean;
  /** Force the amber "pick a study" attention treatment */
  needsAttention?: boolean;
  /** How often the selector replays its attention nudge */
  reminderIntervalMs?: number;
}

const ATTENTION_COLOR = "#D97706";
const ATTENTION_BG = "#FFFBEB";
const CONFIRM_COLOR = "#16A34A";
const CONFIRM_FLASH_MS = 1600;
const DEFAULT_REMINDER_INTERVAL_MS = 4 * 60 * 1000;
const REMINDER_DURATION_MS = 6000;

export const StudyContextSelector: React.FC<StudyContextSelectorProps> = ({
  studies,
  selectedStudyId,
  userStudyRoles,
  onStudyChange,
  className,
  showNoStudyOption = false,
  needsAttention,
  reminderIntervalMs = DEFAULT_REMINDER_INTERVAL_MS,
}) => {
  const [open, setOpen] = useState(false);
  const [justChanged, setJustChanged] = useState(false);
  const [isNudging, setIsNudging] = useState(false);
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value !== selectedStudyId && value !== NO_STUDY_SENTINEL) {
      setJustChanged(true);
    }
    setIsNudging(false);
    onStudyChange(value);
  };

  useEffect(() => {
    if (!justChanged) {
      return;
    }
    const timer = setTimeout(() => setJustChanged(false), CONFIRM_FLASH_MS);
    return () => clearTimeout(timer);
  }, [justChanged]);

  // Recurring reminder so the active study context never fades into the chrome.
  // The cycle restarts whenever the user confirms context by switching studies.
  useEffect(() => {
    if (reminderIntervalMs <= 0) {
      return;
    }
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      if (document.hidden) {
        return;
      }
      setIsNudging(true);
      hideTimer = setTimeout(() => setIsNudging(false), REMINDER_DURATION_MS);
    }, reminderIntervalMs);

    return () => {
      clearInterval(interval);
      if (hideTimer) {
        clearTimeout(hideTimer);
      }
    };
  }, [reminderIntervalMs, selectedStudyId]);

  useEffect(() => {
    if (open) {
      setIsNudging(false);
    }
  }, [open]);

  const selectedStudy = studies.find((s) => s.id === selectedStudyId);
  const currentRole = selectedStudyId
    ? (userStudyRoles[selectedStudyId] ?? "")
    : "";

  // Only show studies where user has roles (filter by ID)
  const displayedStudies = studies.filter(
    (s) => userStudyRoles[s.id] !== undefined,
  );

  // Validate selected value for MUI Select - prevent out-of-range value warnings
  const getValidSelectValue = (): string => {
    // If "No Study Selected" is shown and selected, return it
    if (showNoStudyOption && selectedStudyId === NO_STUDY_SENTINEL) {
      return NO_STUDY_SENTINEL;
    }
    // If selected study exists in displayed studies, return it
    if (
      selectedStudyId &&
      displayedStudies.some((s) => s.id === selectedStudyId)
    ) {
      return selectedStudyId;
    }
    // If no valid selection and "No Study" option is shown, return it
    if (showNoStudyOption) {
      return NO_STUDY_SENTINEL;
    }
    // Otherwise return empty string (will trigger auto-selection in useEffect)
    return "";
  };

  // Ensure selected study is valid when studies or selection changes
  useEffect(() => {
    // Don't auto-select when "No Study Selected" is deliberately chosen
    if (showNoStudyOption && selectedStudyId === NO_STUDY_SENTINEL) {
      return;
    }

    if (displayedStudies.length === 0) {
      return;
    }

    const firstStudy = displayedStudies[0];
    if (!firstStudy) {
      return;
    }

    const isSelectedStudyValid = displayedStudies.some(
      (s) => s.id === selectedStudyId,
    );

    // If no study selected or selected study is invalid, select the first study
    if (!selectedStudyId || !isSelectedStudyValid) {
      onStudyChange(firstStudy.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudyId, displayedStudies, showNoStudyOption]);

  interface RoleConfigEntry {
    color: string;
    icon: React.ReactNode;
    bgcolor: string;
    abbreviation: string;
    displayName: string;
  }

  const ROLE_CONFIG: Record<UserRoleEnum, RoleConfigEntry> = {
    [UserRoleEnum.PRINCIPAL_INVESTIGATOR]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.PRINCIPAL_INVESTIGATOR].color,
      icon: <Person fontSize="small" />,
      bgcolor: "#EFF6FF",
      abbreviation: "PI",
      displayName:
        ROLE_DISPLAY_CONFIG[UserRoleEnum.PRINCIPAL_INVESTIGATOR].name,
    },
    [UserRoleEnum.CRA]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.CRA].color,
      icon: <Visibility fontSize="small" />,
      bgcolor: "#FAF5FF",
      abbreviation: "CRA",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.CRA].name,
    },
    [UserRoleEnum.CTA]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.CTA].color,
      icon: <Assignment fontSize="small" />,
      bgcolor: "#F5F3FF",
      abbreviation: "CTA",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.CTA].name,
    },
    [UserRoleEnum.STUDY_COORDINATOR]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.STUDY_COORDINATOR].color,
      icon: <Assignment fontSize="small" />,
      bgcolor: "#F0FDFA",
      abbreviation: "SC",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.STUDY_COORDINATOR].name,
    },
    [UserRoleEnum.DATA_MANAGER]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.DATA_MANAGER].color,
      icon: <Analytics fontSize="small" />,
      bgcolor: "#ECFDF5",
      abbreviation: "DM",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.DATA_MANAGER].name,
    },
    [UserRoleEnum.SPONSOR]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.SPONSOR].color,
      icon: <Business fontSize="small" />,
      bgcolor: "#FEF2F2",
      abbreviation: "SP",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.SPONSOR].name,
    },
    [UserRoleEnum.PROJECT_MANAGER]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.PROJECT_MANAGER].color,
      icon: <CheckCircle fontSize="small" />,
      bgcolor: "#FFFBEB",
      abbreviation: "PM",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.PROJECT_MANAGER].name,
    },
    [UserRoleEnum.TENANT]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.TENANT].color,
      icon: <Person fontSize="small" />,
      bgcolor: "#EFF6FF",
      abbreviation: "TA",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.TENANT].name,
    },
    [UserRoleEnum.SITE_ADMIN]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.SITE_ADMIN].color,
      icon: <Assignment fontSize="small" />,
      bgcolor: "#F0FDFA",
      abbreviation: "SA",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.SITE_ADMIN].name,
    },
    [UserRoleEnum.QA_QC]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.QA_QC].color,
      icon: <CheckCircle fontSize="small" />,
      bgcolor: "#FEF2F2",
      abbreviation: "QA",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.QA_QC].name,
    },
    [UserRoleEnum.ACCOUNTANT]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.ACCOUNTANT].color,
      icon: <Analytics fontSize="small" />,
      bgcolor: "#F0FDFA",
      abbreviation: "AC",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.ACCOUNTANT].name,
    },
    [UserRoleEnum.FM]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.FM].color,
      icon: <Analytics fontSize="small" />,
      bgcolor: "#EEF2FF",
      abbreviation: "FM",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.FM].name,
    },
    [UserRoleEnum.REGULATORY]: {
      color: ROLE_DISPLAY_CONFIG[UserRoleEnum.REGULATORY].color,
      icon: <Assignment fontSize="small" />,
      bgcolor: "#FEF2F2",
      abbreviation: "RG",
      displayName: ROLE_DISPLAY_CONFIG[UserRoleEnum.REGULATORY].name,
    },
  };

  const DEFAULT_ROLE_CONFIG: RoleConfigEntry = {
    color: "#6B7280",
    icon: <Assignment fontSize="small" />,
    bgcolor: "#F9FAFB",
    abbreviation: "U",
    displayName: "User",
  };

  const getRoleConfig = (role: string): RoleConfigEntry => {
    const normalized = normalizeRole(role);
    return ROLE_CONFIG[normalized] ?? DEFAULT_ROLE_CONFIG;
  };

  if (studies.length === 0 && !showNoStudyOption) {
    return (
      <Box
        className={className}
        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
      >
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontFamily: CLINICAL_FONT_FAMILY,
            fontWeight: 600,
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            display: { xs: "none", lg: "block" },
            minWidth: "fit-content",
          }}
        >
          Study Context
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            backgroundColor: "#FEF2F2",
            border: "1.5px solid #FECACA",
            borderRadius: 2.5,
            fontFamily: CLINICAL_FONT_FAMILY,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#DC2626",
              fontFamily: CLINICAL_FONT_FAMILY,
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          >
            No Active Studies Available
          </Typography>
        </Box>
      </Box>
    );
  }

  const roleConfig = getRoleConfig(currentRole ?? "");

  const hasStudySelected =
    !!selectedStudyId &&
    selectedStudyId !== NO_STUDY_SENTINEL &&
    !!selectedStudy;
  const showAttention = needsAttention ?? !hasStudySelected;
  const accentColor = justChanged
    ? CONFIRM_COLOR
    : showAttention
      ? ATTENTION_COLOR
      : roleConfig.color;
  const accentBg = justChanged
    ? "#F0FDF4"
    : showAttention
      ? ATTENTION_BG
      : roleConfig.bgcolor;

  return (
    <Box
      className={className}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: showAttention ? ATTENTION_COLOR : theme.palette.text.secondary,
          fontFamily: CLINICAL_FONT_FAMILY,
          fontWeight: 600,
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          display: { xs: "none", lg: "flex" },
          alignItems: "center",
          gap: 0.5,
          minWidth: "fit-content",
          transition: "color 0.3s ease",
        }}
      >
        {/* Always-on "live context" dot so the selector never reads as static chrome */}
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: accentColor,
            flexShrink: 0,
            animation: "studyContextHeartbeat 2.8s ease-in-out infinite",
            "@keyframes studyContextHeartbeat": {
              "0%, 100%": { opacity: 1, transform: "scale(1)" },
              "50%": { opacity: 0.35, transform: "scale(0.75)" },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
        Study Context
      </Typography>

      <Tooltip
        arrow
        placement="bottom"
        open={isNudging && !open}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={
          hasStudySelected
            ? `You are working in “${selectedStudy?.title ?? ""}” — switch here if this isn't the right study.`
            : "No study selected — choose one here before you start working."
        }
        slotProps={{
          tooltip: {
            sx: {
              fontFamily: CLINICAL_FONT_FAMILY,
              fontSize: "0.72rem",
              fontWeight: 500,
              maxWidth: 260,
              backgroundColor: "#0F172A",
              px: 1.5,
              py: 1,
            },
          },
          arrow: { sx: { color: "#0F172A" } },
        }}
      >
        <FormControl size="small" sx={{ width: "100%", minWidth: 0 }}>
          <Select
            value={getValidSelectValue()}
            onChange={handleChange}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            displayEmpty
            IconComponent={() => (
              <KeyboardArrowDown
                sx={{
                  color: showAttention
                    ? ATTENTION_COLOR
                    : theme.palette.text.secondary,
                  fontSize: 18,
                  mr: 0.5,
                  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            )}
            sx={{
              width: "100%",
              minWidth: 0,
              "& .MuiSelect-select": {
                py: 1.25,
                px: 1.5,
                backgroundColor: showAttention ? accentBg : `${accentBg}30`,
                border: "1.5px solid",
                borderColor: showAttention ? accentColor : `${accentColor}40`,
                borderRadius: 2.5,
                fontFamily: CLINICAL_FONT_FAMILY,
                fontSize: "0.9rem",
                display: "flex",
                alignItems: "center",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                // Draws the eye when nothing is selected, or confirms a switch
                animation:
                  showAttention && !open
                    ? "studySelectorAttention 2.2s ease-in-out infinite"
                    : justChanged
                      ? "studySelectorConfirm 1.6s ease-out 1"
                      : isNudging
                        ? "studySelectorNudge 1.6s ease-in-out 3"
                        : "none",
                "@keyframes studySelectorAttention": {
                  "0%, 100%": {
                    boxShadow: `0 0 0 0 ${ATTENTION_COLOR}55`,
                  },
                  "50%": {
                    boxShadow: `0 0 0 8px ${ATTENTION_COLOR}00`,
                  },
                },
                "@keyframes studySelectorConfirm": {
                  "0%": { boxShadow: `0 0 0 0 ${CONFIRM_COLOR}66` },
                  "100%": { boxShadow: `0 0 0 10px ${CONFIRM_COLOR}00` },
                },
                "@keyframes studySelectorNudge": {
                  "0%, 100%": {
                    boxShadow: `0 0 0 0 ${accentColor}00`,
                    transform: "translateY(0)",
                  },
                  "35%": {
                    boxShadow: `0 0 0 6px ${accentColor}40`,
                    transform: "translateY(-2px)",
                  },
                  "70%": {
                    boxShadow: `0 0 0 10px ${accentColor}00`,
                    transform: "translateY(0)",
                  },
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
                "&:hover": {
                  borderColor: showAttention ? accentColor : `${accentColor}80`,
                  backgroundColor: showAttention ? accentBg : `${accentBg}50`,
                  boxShadow: `0 2px 8px ${accentColor}20`,
                },
                "&:focus": {
                  borderColor: accentColor,
                  backgroundColor: showAttention ? accentBg : `${accentBg}60`,
                  boxShadow: `0 4px 12px ${accentColor}30`,
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1,
                  border: "1.5px solid",
                  borderColor: `${accentColor}40`,
                  borderRadius: 2.5,
                  boxShadow: "0 12px 24px -4px rgba(0, 0, 0, 0.15)",
                },
              },
              slotProps: {
                paper: {
                  style: {
                    maxHeight: 350,
                  },
                },
              },
              disableScrollLock: true,
            }}
            renderValue={(selected) => {
              // "No Study Selected" state
              if (selected === NO_STUDY_SENTINEL) {
                return (
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: showAttention ? "#FEF3C7" : "#F3F4F6",
                        color: showAttention ? ATTENTION_COLOR : "#6B7280",
                      }}
                    >
                      {showAttention ? (
                        <ErrorOutline fontSize="small" />
                      ) : (
                        <Science fontSize="small" />
                      )}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: showAttention ? ATTENTION_COLOR : "#6B7280",
                          fontFamily: CLINICAL_FONT_FAMILY,
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        No Study Selected
                      </Typography>
                      {showAttention && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: ATTENTION_COLOR,
                            fontFamily: CLINICAL_FONT_FAMILY,
                            fontSize: "0.62rem",
                            fontWeight: 500,
                            display: "block",
                          }}
                        >
                          Pick a study to unlock your workspace
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                );
              }

              if (!selected || !selectedStudy) {
                return (
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: showAttention
                          ? "#FEF3C7"
                          : theme.palette.grey[100],
                        color: showAttention
                          ? ATTENTION_COLOR
                          : theme.palette.text.secondary,
                      }}
                    >
                      {showAttention ? (
                        <ErrorOutline fontSize="small" />
                      ) : (
                        <Science fontSize="small" />
                      )}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: showAttention
                            ? ATTENTION_COLOR
                            : theme.palette.text.secondary,
                          fontFamily: CLINICAL_FONT_FAMILY,
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        Select Study
                      </Typography>
                      {showAttention && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: ATTENTION_COLOR,
                            fontFamily: CLINICAL_FONT_FAMILY,
                            fontSize: "0.62rem",
                            fontWeight: 500,
                            display: "block",
                          }}
                        >
                          Required to continue
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                );
              }

              return (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.25}
                  sx={{ width: "100%" }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: roleConfig.color,
                      color: "white",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      fontFamily: CLINICAL_FONT_FAMILY,
                      boxShadow: `0 2px 8px ${roleConfig.color}40`,
                    }}
                  >
                    {roleConfig.abbreviation}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: CLINICAL_FONT_FAMILY,
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedStudy.title}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      mt={0.25}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: CLINICAL_FONT_FAMILY,
                          color: theme.palette.text.secondary,
                          fontSize: "0.65rem",
                          fontWeight: 500,
                        }}
                      >
                        {convertApiPhaseToDisplay(
                          selectedStudy.phase as ApiPhaseKey,
                        )}
                      </Typography>
                      <Chip
                        label={currentRole}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: "0.6rem",
                          fontFamily: CLINICAL_FONT_FAMILY,
                          fontWeight: 600,
                          backgroundColor: roleConfig.bgcolor,
                          color: roleConfig.color,
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              );
            }}
          >
            {showNoStudyOption && (
              <MenuItem
                key={NO_STUDY_SENTINEL}
                value={NO_STUDY_SENTINEL}
                sx={{
                  py: 1.25,
                  px: 1.5,
                  mb: 0.5,
                  borderRadius: 1.5,
                  transition: "all 0.2s ease",
                  backgroundColor:
                    selectedStudyId === NO_STUDY_SENTINEL
                      ? "#F3F4F640"
                      : "transparent",
                  "&:hover": { backgroundColor: "#F3F4F660" },
                  "&.Mui-selected": {
                    backgroundColor: "#F3F4F650",
                    "&:hover": { backgroundColor: "#F3F4F670" },
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  width="100%"
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: "#E5E7EB",
                      color: "#6B7280",
                      fontSize: "0.8rem",
                    }}
                  >
                    <Science fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontFamily: CLINICAL_FONT_FAMILY,
                        fontWeight: 600,
                        color: "#6B7280",
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        mb: 0.25,
                      }}
                    >
                      No Study Selected
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: CLINICAL_FONT_FAMILY,
                        color: "#9CA3AF",
                        fontSize: "0.65rem",
                        fontWeight: 500,
                        display: "block",
                      }}
                    >
                      Restricted access mode
                    </Typography>
                  </Box>
                </Stack>
              </MenuItem>
            )}
            {displayedStudies.map((study) => {
              const role = userStudyRoles[study.id] ?? "";
              const studyRoleConfig = getRoleConfig(role);
              const isSelected = study.id === selectedStudyId;

              return (
                <MenuItem
                  key={study.id}
                  value={study.id}
                  sx={{
                    py: 1.25,
                    px: 1.5,
                    mb: 0.5,
                    borderRadius: 1.5,
                    transition: "all 0.2s ease",
                    backgroundColor: isSelected
                      ? `${studyRoleConfig.bgcolor}40`
                      : "transparent",
                    "&:hover": {
                      backgroundColor: `${studyRoleConfig.bgcolor}60`,
                    },
                    "&.Mui-selected": {
                      backgroundColor: `${studyRoleConfig.bgcolor}50`,
                      "&:hover": {
                        backgroundColor: `${studyRoleConfig.bgcolor}70`,
                      },
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    width="100%"
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: studyRoleConfig.color,
                        color: "white",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        fontFamily: CLINICAL_FONT_FAMILY,
                        boxShadow: `0 2px 8px ${studyRoleConfig.color}40`,
                      }}
                    >
                      {studyRoleConfig.abbreviation}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontFamily: CLINICAL_FONT_FAMILY,
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          fontSize: "0.9rem",
                          lineHeight: 1.2,
                          mb: 0.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {study.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: CLINICAL_FONT_FAMILY,
                          color: theme.palette.text.secondary,
                          fontSize: "0.65rem",
                          fontWeight: 500,
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Protocol: {study.protocolNumber}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: CLINICAL_FONT_FAMILY,
                            color: theme.palette.text.secondary,
                            fontSize: "0.65rem",
                            fontWeight: 500,
                          }}
                        >
                          {convertApiPhaseToDisplay(study.phase as ApiPhaseKey)}
                        </Typography>
                        <Chip
                          label={role}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            fontFamily: CLINICAL_FONT_FAMILY,
                            fontWeight: 600,
                            backgroundColor: studyRoleConfig.bgcolor,
                            color: studyRoleConfig.color,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Tooltip>
    </Box>
  );
};

export default StudyContextSelector;
